import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Music2, Mail, Phone, ArrowRight, Newspaper } from 'lucide-react';
import logoEssencia from '/lovable-uploads/e103bf0a-04c5-4266-926c-c5c3c3d46f35.png';
import NewsletterForm from './NewsletterForm';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useQueryClient } from '@tanstack/react-query';

const LatestNewsBlock = () => {
  const queryClient = useQueryClient();
  const noticias = queryClient.getQueryData<any[]>(['noticias-publicas']);
  const latest = noticias?.[0];
  if (!latest) return null;
  const formattedDate = new Date(latest.created_at || latest.data_publicacao).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  return (
    <div className="border-t border-white/[0.06]">
      <div className="container mx-auto px-4 py-4 flex items-center gap-3">
        <Newspaper size={14} className="text-white/30 flex-shrink-0" />
        <span className="text-[10px] uppercase tracking-widest text-white/25 font-medium flex-shrink-0">Última notícia</span>
        <Link to={`/noticia/${latest.slug || latest.id}`} className="text-sm text-white/50 hover:text-white/80 transition-colors truncate">
          {latest.titulo}
        </Link>
        <span className="text-[10px] text-white/20 flex-shrink-0 ml-auto">{formattedDate}</span>
      </div>
    </div>
  );
};

const Footer = () => {
  const { spotifyPlaylist } = useSiteConfig();

  const quickLinks = [
    { name: 'Início', path: '/' },
    { name: 'Bandas', path: '/bandas' },
    { name: 'Notícias', path: '/noticias' },
    { name: 'Agenda de eventos', path: '/agenda-eventos' },
    { name: 'Contato', path: '/contato' },
    { name: 'Política de Privacidade', path: '/politica-privacidade' },
  ];

  const socialLinks = [
    { name: 'Instagram', url: 'https://www.instagram.com/essenciadasbandas/', icon: Instagram },
    { name: 'Facebook', url: 'https://www.facebook.com/essenciadasbandas', icon: Facebook },
    { name: 'YouTube', url: 'https://youtube.com/channel/UCrXw3srfVvTA575r8YxAB6A', icon: Youtube },
    { name: 'Spotify', url: spotifyPlaylist?.url || 'https://open.spotify.com/playlist/66LDa1Cfw6HdAKGYOYB0TK', icon: Music2 },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Background gradient premium */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsl(221,89%,6%) 0%, hsl(280,59%,8%) 50%, hsl(221,89%,4%) 100%)' }} />

      {/* Orbs decorativos */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 right-20 w-64 h-64 rounded-full opacity-10 blur-[100px]" style={{ background: 'radial-gradient(circle, hsl(280,59%,45%), transparent)' }} />
        <div className="absolute bottom-10 left-20 w-48 h-48 rounded-full opacity-10 blur-[80px]" style={{ background: 'radial-gradient(circle, hsl(221,80%,35%), transparent)' }} />
      </div>

      <div className="relative z-10">
        {/* Top border glow */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand Section */}
            <div className="lg:col-span-2 space-y-6">
              <Link to="/" className="inline-block">
                <img
                  src={logoEssencia}
                  alt="Essência das Bandas"
                  className="h-14 w-auto hover:opacity-80 transition-opacity"
                  width={170}
                  height={56}
                  loading="lazy"
                />
              </Link>
              <p className="text-white/50 max-w-md leading-relaxed text-sm">
                Conectamos o Sul do Brasil através da música. Divulgação, representação e fortalecimento da cultura das bandas desde 2018.
              </p>

              {/* Contact */}
              <div className="space-y-2.5">
                <a href="mailto:contato@essenciadasbandas.com" className="flex items-center gap-2.5 text-white/40 hover:text-white/80 transition-colors text-sm group">
                  <Mail size={14} className="group-hover:text-white/60" />
                  <span>contato@essenciadasbandas.com</span>
                </a>
                <a href="tel:+5554996953904" className="flex items-center gap-2.5 text-white/40 hover:text-white/80 transition-colors text-sm group">
                  <Phone size={14} className="group-hover:text-white/60" />
                  <span>(54) 99695.3904</span>
                </a>
              </div>

              {/* Social icons */}
              <div className="flex gap-3 pt-2">
                {socialLinks.map(social => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.08] hover:border-white/[0.12] transition-all duration-300 hover:scale-110"
                      aria-label={social.name}
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xs font-semibold text-white/30 uppercase tracking-[0.2em] mb-6">Navegação</h3>
              <ul className="space-y-3">
                {quickLinks.map(link => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-white/45 hover:text-white text-sm transition-colors duration-200 inline-flex items-center gap-1.5 group"
                    >
                      <ArrowRight size={10} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="text-xs font-semibold text-white/30 uppercase tracking-[0.2em] mb-6">Newsletter</h3>
              <p className="text-white/40 text-sm mb-4 leading-relaxed">
                Receba novidades e eventos diretamente no seu e-mail.
              </p>
              <NewsletterForm />
            </div>
          </div>
        </div>

        {/* Latest News */}
        <LatestNewsBlock />

        {/* Bottom */}
        <div className="border-t border-white/[0.06]">
          <div className="container mx-auto px-4 py-5 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-xs text-white/30">
              © {new Date().getFullYear()} Essência das Bandas – Todos os direitos reservados.
            </p>
            <p className="text-xs text-white/20">
              CNPJ: 52.891.647/0001-54
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
